import type { GetUserProps } from '@/apis/core/user/get/getUser';
import { getUser } from '@/apis/core/user/get/getUser';

export const UNKNOWN_USER_DISPLAY_NAME = 'Unknown';

export interface UserNameLike {
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  preferredName?: string | null;
  displayName?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  email?: string | null;
}

/**
 * Resolves the best possible display name from a user-like record.
 * Tries a full name (first + last) first, then common name fields,
 * then email-style identifiers, and finally falls back to a generic label
 * instead of exposing a raw user ID.
 */
export const getUserDisplayNameFromRecord = (
  user: UserNameLike | null | undefined,
  fallback: string = UNKNOWN_USER_DISPLAY_NAME
): string => {
  if (!user || typeof user !== 'object') return fallback;

  const fullName = [user.firstName, user.lastName]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0)
    .join(' ')
    .trim();

  const candidate =
    fullName ||
    (typeof user.name === 'string' ? user.name.trim() : '') ||
    (typeof user.preferredName === 'string' ? user.preferredName.trim() : '') ||
    (typeof user.displayName === 'string' ? user.displayName.trim() : '') ||
    (typeof user.userName === 'string' ? user.userName.trim() : '') ||
    (typeof user.userEmail === 'string' ? user.userEmail.trim() : '') ||
    (typeof user.email === 'string' ? user.email.trim() : '');

  return candidate.length > 0 ? candidate : fallback;
}

/**
 * High-level helper that:
 * - Uses existing user record when available
 * - If we only have a userId, attempts to fetch user data via getUser()
 * - Finally falls back to "Unknown" when we still cannot resolve a name
 *
 * Note: getUser() returns the *current* user; call sites should only rely on
 * this when userId is known to be the current user's id.
 */
export const getUserDisplayNameWithFetch = async (options: {
  userId?: string | null;
  userRecord?: UserNameLike | null;
  getAccessToken?: GetUserProps['getAccessToken'];
}): Promise<string> => {
  const { userId, userRecord, getAccessToken } = options;

  // 1) Try the existing record first
  const fromRecord = getUserDisplayNameFromRecord(userRecord, '');
  if (fromRecord && fromRecord.length > 0) {
    return fromRecord;
  }

  // 2) If we have a userId but not data, and can call the API, try getUser()
  if (userId && getAccessToken) {
    try {
      const apiUser = await getUser({ getAccessToken });
      const fromApi = getUserDisplayNameFromRecord(apiUser, '');
      if (fromApi && fromApi.length > 0) {
        return fromApi;
      }
    } catch {
      // Swallow; we'll fall back to Unknown below
    }
  }

  // 3) Final fallback: never expose raw IDs, just "Unknown"
  return UNKNOWN_USER_DISPLAY_NAME;
}


